/**
 * JWT 토큰 관리 유틸리티
 * 모든 JWT 디코딩, 검증, 파싱 로직을 통합 관리합니다.
 */

import { logger } from './logger'

export interface JWTPayload {
  agree: boolean
  aud: string // 'AI Manager MVP'
  exp: number // 1763599357
  iat: number // 1763512957
  iss: string // 'AI Manager MVP'
  sub: number // '335'
  type: string // 'access'
}

/**
 * JWT 토큰 디코딩 (통합 함수)
 * @param token - JWT 토큰 문자열
 * @returns 디코딩된 payload 또는 null
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      logger.warn('잘못된 JWT 형식: 3개의 파트가 필요합니다')
      return null
    }

    // Base64 URL 디코딩
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')

    // URI 디코딩 (한글 등 특수문자 지원)
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    logger.error('JWT 디코딩 실패', error)
    return null
  }
}

/**
 * JWT 토큰에서 만료 시간 추출 (밀리초)
 * @param token - JWT 토큰 문자열
 * @returns 만료 시간(밀리초) 또는 null
 */
export function getTokenExpiration(token: string): number | null {
  const payload = decodeJWT(token)
  if (!payload?.exp) {
    logger.warn('토큰에 만료 시간(exp) 없음')
    return null
  }

  return payload.exp * 1000 // 초 -> 밀리초 변환
}

/**
 * JWT 토큰 유효성 확인 (만료 여부 + 버퍼 시간)
 * @param token - JWT 토큰 문자열 또는 null
 * @param bufferSeconds - 만료 전 여유 시간 (기본 30초)
 * @returns 토큰이 유효한지 여부
 */
export function isTokenValid(token: string | null, bufferSeconds: number = 30): boolean {
  if (!token) {
    return false
  }

  const exp = getTokenExpiration(token)
  if (!exp) {
    return false
  }

  const now = Date.now()
  const buffer = bufferSeconds * 1000

  // 만료 시간 - 현재 시간 > 버퍼 시간
  return exp - now > buffer
}

/**
 * JWT 토큰에서 user_id 추출
 * 다양한 필드명 지원: user_id, userId, sub, id, uid
 * @param token - JWT 토큰 문자열
 * @returns user_id 또는 null
 */
export function getUserIdFromToken(token: string): number | null {
  const payload = decodeJWT(token)
  if (!payload) {
    return null
  }

  // 다양한 필드명 시도
  const userId = payload.sub

  if (!userId) {
    logger.warn('토큰에 user_id 필드 없음', payload)
    return null
  }

  // 숫자로 변환
  return typeof userId === 'number' ? userId : parseInt(String(userId), 10)
}

/**
 * JWT 만료까지 남은 시간 (초)
 * @param token - JWT 토큰 문자열
 * @returns 남은 시간(초) 또는 null
 */
export function getTokenRemainingTime(token: string): number | null {
  const exp = getTokenExpiration(token)
  if (!exp) {
    return null
  }

  const remaining = exp - Date.now()
  return Math.max(0, Math.floor(remaining / 1000))
}

/**
 * JWT 토큰이 만료되었는지 확인
 * @param token - JWT 토큰 문자열
 * @returns 만료되었으면 true
 */
export function isTokenExpired(token: string): boolean {
  const exp = getTokenExpiration(token)
  if (!exp) {
    return true
  }

  return Date.now() >= exp
}
